import { Fragment, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiMapPin } from "react-icons/fi";
import { StatusBadge } from "../Rfid/utils/statusBadge";
import { getReportRowKey, useRfidReportsStore } from "./store/useRfidReportsStore";

const FIGMA = {
  navy: "var(--color-primary)",
  orange: "#F5A623",
  textMuted: "#6B7280",
  expandBg: "#F1F5F9",
  subHeaderBg: "rgba(var(--color-primary-rgb), 0.04)",
  subHeaderText: "var(--color-primary)",
  border: "#eef0f3",
};

// Same look as the shared tables (theme.css): light tinted header with
// primary-colour text, white rows separated by a thin divider, no grid lines.
const HEAD_BG =
  "linear-gradient(rgba(var(--color-primary-rgb), 0.07), rgba(var(--color-primary-rgb), 0.07)), #ffffff";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getVisiblePageNumbers = (currentPage, totalPages, maxVisible = 7) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const MAIN_COLUMNS = [
  "S.No",
  "View On Map",
  "Asset Code",
  "Asset Type",
  "Asset Category",
  "IMEI/SL/MFG",
  "Tag Vehicle No",
  "Zone",
  "Ward",
  "Scanned By Reader",
  "Reader Category",
  "Reader Vehicle",
  "Reader Zone",
  "Reader Ward",
  "Total Scanned",
  "Actual Shift",
  "Last Scanned Time",
  "In Time",
  "Out Time",
];

const SCAN_DETAIL_COLUMNS = [
  "S.No",
  "Tag ID",
  "Reader ID",
  "Scanned On",
  "Actual Shift",
  "Reader Installed Area",
  "Reader Vehicle",
  "Reader Category",
  "Reader Zone",
  "Reader Ward",
  "Status",
  "From Date & Time",
  "To Date & Time",
];

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  whiteSpace: "nowrap",
  border: "none",
  background: HEAD_BG,
  color: "var(--color-primary)",
  padding: "12px 14px",
  fontSize: "12.5px",
  fontWeight: 600,
  fontFamily: '"Poppins", sans-serif',
  textAlign: "center",
};

const tdStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  textAlign: "center",
  color: "#4b5565",
  fontSize: "13px",
  backgroundColor: "#FFFFFF",
};

const expandTdStyle = {
  ...tdStyle,
  width: 36,
  minWidth: 36,
  maxWidth: 36,
  padding: "6px 4px",
};

const subThStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
  backgroundColor: FIGMA.subHeaderBg,
  color: FIGMA.subHeaderText,
};

const subTdStyle = {
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  textAlign: "center",
  color: "#4b5565",
  backgroundColor: "#FFFFFF",
  whiteSpace: "nowrap",
};

const subStatusThStyle = {
  ...subThStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "8px 6px",
};

const subStatusTdStyle = {
  ...subTdStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "6px 4px",
};

const formatScanDateTime = (value) => {
  if (!value) return "N/A";
  const raw = String(value).trim();
  if (!raw) return "N/A";

  // API may return "2026-07-31 10:55:01" — normalize for Date parsing
  const normalized =
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) ? raw.replace(" ", "T") : raw;

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const mapRowToReport = (row, index = 0, scannedTags = []) => {
  const serial = index + 1;
  const rfidTag =
    row?.rfidTag ??
    row?.RfidTag ??
    row?.tagName ??
    row?.otherNo ??
    `Tag-${String(serial).padStart(3, "0")}`;

  const scanDetails =
    scannedTags.length > 0
      ? scannedTags
      : row?.scanDetails?.length
      ? row.scanDetails
      : row?.ScanDetails?.length
      ? row.ScanDetails
      : [];

  const display = (value, fallback = "N/A") => {
    if (value == null) return fallback;
    const text = String(value).trim();
    return text === "" ? fallback : text;
  };

  return {
    ...row,
    rfidTag,
    rfidCode: display(row?.rfidCode ?? row?.assetCode, "-"),
    rfidCategoryName: display(
      row?.rfidCategoryName ?? row?.assetCategory ?? row?.assetCategoryName
    ),
    assetTypeName: display(
      row?.assetTypeName ??
        row?.assetType ??
        row?.rfidTypeName ??
        row?.AssetTypeName ??
        row?.RFIDType
    ),
    imeiSlMfgNo: display(
      row?.imeiSlMfgNo ?? row?.imeiOrSerialOrMfgNo ?? row?.imeiNo
    ),
    vehicleNo: display(
      row?.vehicleNo ??
        row?.tagVehicleNo ??
        row?.TagVehicleNo ??
        row?.vehicleNumber
    ),
    zoneName: display(row?.zoneName ?? row?.zone ?? row?.zoneNo),
    wardNo: display(row?.wardNo ?? row?.wardName ?? row?.circleName),
    scannedByReader: display(
      row?.scannedByReader ?? row?.ScannedByReader ?? row?.rfidDeviceName
    ),
    readerCategory: display(
      row?.readerCategory ?? row?.ReaderCategory ?? row?.rfidCategoryName
    ),
    readerVehicle: display(
      row?.readerVehicle ?? row?.readerVehicleNo ?? row?.ReaderVehicle
    ),
    readerZone: display(
      row?.readerZone ?? row?.readerZoneNo ?? row?.ReaderZone
    ),
    readerWard: display(
      row?.readerWard ??
        row?.ReaderWard ??
        row?.readerWardNo ??
        row?.ReaderWardNo
    ),
    totalScanned:
      row?.totalScanned ??
      row?.TotalScanned ??
      row?.scannedCount ??
      row?.TotalScan ??
      0,
    actualShift: display(
      row?.actualShift ?? row?.shiftName ?? row?.ShiftName ?? row?.shift
    ),
    lastScannedOn: display(
      row?.lastScannedOn ?? row?.lastScannedTime ?? row?.LastScannedOn,
      ""
    ),
    inTime: display(row?.inTime ?? row?.InTime, ""),
    outTime: display(row?.outTime ?? row?.OutTime, ""),
    scanDetails,
  };
};

const ExpandButton = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isExpanded ? "Collapse row" : "Expand row"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: 3,
      border: `1px solid ${isExpanded ? FIGMA.orange : "#D1D5DB"}`,
      backgroundColor: "#FFFFFF",
      color: isExpanded ? FIGMA.orange : "#6B7280",
      fontSize: "12px",
      fontWeight: 500,
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
    }}
  >
    {isExpanded ? "−" : "+"}
  </button>
);

const RfidReportsExpandableTable = ({ rows = [], onViewMap }) => {
  const {
    pagination,
    setReportPage,
    setReportPageSize,
    rfidScannedTags,
    isScannedTagsLoading,
    fetchRfMasterScannedTags,
    isLoading,
  } = useRfidReportsStore();
  const [expandedIds, setExpandedIds] = useState([]);

  const { pageNumber, pageSize, totalRecords, totalPages } = pagination;
  const currentPage = pageNumber;
  const rowsPerPage = pageSize;

  const getRowId = (row, index = 0) => getReportRowKey(row, index);

  const reportRows = useMemo(
    () =>
      (rows ?? []).map((row, index) => {
        const rowId = getRowId(row, index);
        return mapRowToReport(row, index, rfidScannedTags[rowId] || []);
      }),
    [rows, rfidScannedTags]
  );

  const pageNumbers = useMemo(
    () => getVisiblePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const paginatedRows = reportRows;
  const hasData = paginatedRows.length > 0;
  const totalResults =
    typeof totalRecords === "number" ? totalRecords : paginatedRows.length;
  const rangeStart = hasData ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const rangeEnd = hasData
    ? Math.min(currentPage * rowsPerPage, totalResults)
    : 0;

  useEffect(() => {
    setExpandedIds([]);
  }, [rows]);

  const toggleExpand = async (row, rowIndex = 0) => {
    const rowId = getRowId(row, rowIndex);
    const isExpanded = expandedIds.includes(rowId);

    if (isExpanded) {
      setExpandedIds((prev) => prev.filter((id) => id !== rowId));
      return;
    }

    setExpandedIds((prev) => [...prev, rowId]);
    await fetchRfMasterScannedTags(row, rowIndex);
  };

  const handleViewMap = (row) => {
    if (!row || !onViewMap) return;
    const lat = Number(row?.latitude ?? row?.Latitude);
    const lng = Number(row?.longitude ?? row?.Longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    onViewMap(row);
  };

  return (
    <div>
      <div
        style={{
          overflow: "hidden",
          borderRadius: 16,
          border: `1px solid ${FIGMA.border}`,
          backgroundColor: "#FFFFFF",
          padding: "8px 10px 4px",
          boxShadow:
            "0 1px 2px rgba(17, 24, 39, 0.03), 0 6px 20px rgba(17, 24, 39, 0.05)",
        }}
      >
        <div
          style={{
            maxHeight: hasData ? "calc(100vh - 320px)" : "none",
            overflow: "auto",
          }}
        >
          <table
            style={{
              minWidth: 2000,
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              textAlign: "center",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    ...thStyle,
                    width: 36,
                    minWidth: 36,
                    maxWidth: 36,
                    padding: "8px 4px",
                  }}
                />
                {MAIN_COLUMNS.map((col) => (
                  <th key={col} style={thStyle}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!hasData ? (
                <tr>
                  <td
                    colSpan={MAIN_COLUMNS.length + 1}
                    style={{
                      ...tdStyle,
                      padding: "16px 24px",
                      color: FIGMA.textMuted,
                      fontWeight: 500,
                    }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, rowIndex) => {
                  const rowId = getRowId(row, rowIndex);
                  const serialNo =
                    row?.rowNumber ??
                    (currentPage - 1) * rowsPerPage + rowIndex + 1;
                  const isExpanded = expandedIds.includes(rowId);
                  const scanDetails = row.scanDetails ?? [];

                  return (
                    <Fragment key={rowId}>
                      <tr>
                        <td style={expandTdStyle}>
                          <ExpandButton
                            isExpanded={isExpanded}
                            onClick={() => toggleExpand(row, rowIndex)}
                          />
                        </td>
                        <td style={tdStyle}>{serialNo}</td>
                        <td style={tdStyle}>
                          {(() => {
                            const lat = Number(row?.latitude ?? row?.Latitude);
                            const lng = Number(row?.longitude ?? row?.Longitude);
                            const hasCoords =
                              !Number.isNaN(lat) && !Number.isNaN(lng);

                            return (
                              <button
                                type="button"
                                title={
                                  hasCoords
                                    ? "View on map"
                                    : "Coordinates not available"
                                }
                                disabled={!hasCoords}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!hasCoords) return;
                                  handleViewMap(row);
                                }}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  cursor: hasCoords ? "pointer" : "not-allowed",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  padding: 0,
                                }}
                              >
                                <FiMapPin
                                  size={14}
                                  color={hasCoords ? "#EC1117" : "#CBD5E1"}
                                />
                                <span
                                  style={{
                                    color: hasCoords ? "rgb(72, 55, 55)" : "#CBD5E1",
                                  }}
                                >
                                  View
                                </span>
                              </button>
                            );
                          })()}
                        </td>
                        <td style={tdStyle}>{row.rfidCode || "N/A"}</td>
                        <td style={tdStyle}>{row.assetTypeName || "N/A"}</td>
                        <td style={tdStyle}>{row.rfidCategoryName || "N/A"}</td>
                        <td style={tdStyle}>{row.imeiSlMfgNo || "N/A"}</td>
                        <td style={tdStyle}>{row.vehicleNo || "N/A"}</td>
                        <td style={tdStyle}>{row.zoneName || "N/A"}</td>
                        <td style={tdStyle}>{row.wardNo || "N/A"}</td>
                        <td style={tdStyle}>{row.scannedByReader || "N/A"}</td>
                        <td style={tdStyle}>{row.readerCategory || "N/A"}</td>
                        <td style={tdStyle}>{row.readerVehicle || "N/A"}</td>
                        <td style={tdStyle}>{row.readerZone || "N/A"}</td>
                        <td style={tdStyle}>{row.readerWard || "N/A"}</td>
                        <td style={tdStyle}>{row.totalScanned ?? "N/A"}</td>
                        <td style={tdStyle}>{row.actualShift || "N/A"}</td>
                        <td style={tdStyle}>
                          {formatScanDateTime(row.lastScannedOn)}
                        </td>
                        <td style={tdStyle}>
                          {formatScanDateTime(row.inTime)}
                        </td>
                        <td style={tdStyle}>
                          {formatScanDateTime(row.outTime)}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr style={{ backgroundColor: FIGMA.expandBg }}>
                          <td
                            colSpan={MAIN_COLUMNS.length + 1}
                            style={{
                              border: `1px solid ${FIGMA.border}`,
                              padding: "16px 24px",
                            }}
                          >
                            <div style={{ overflowX: "auto" }}>
                              <table
                                style={{
                                  width: "100%",
                                  minWidth: 1200,
                                  borderCollapse: "collapse",
                                  textAlign: "left",
                                  fontSize: "13px",
                                }}
                              >
                                <thead>
                                  <tr>
                                    {SCAN_DETAIL_COLUMNS.map((col) => (
                                      <th
                                        key={col}
                                        style={
                                          col === "Status"
                                            ? subStatusThStyle
                                            : subThStyle
                                        }
                                      >
                                        {col}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {isScannedTagsLoading && isExpanded && !scanDetails.length ? (
                                    <tr>
                                      <td
                                        colSpan={SCAN_DETAIL_COLUMNS.length}
                                        style={{
                                          ...subTdStyle,
                                          textAlign: "center",
                                        }}
                                      >
                                        Loading scan details...
                                      </td>
                                    </tr>
                                  ) : !scanDetails.length ? (
                                    <tr>
                                      <td
                                        colSpan={SCAN_DETAIL_COLUMNS.length}
                                        style={{
                                          ...subTdStyle,
                                          textAlign: "center",
                                        }}
                                      >
                                        No scan details available
                                      </td>
                                    </tr>
                                  ) : (
                                    scanDetails.map((item, index) => (
                                      <tr key={index}>
                                        <td style={subTdStyle}>{index + 1}</td>
                                        <td style={subTdStyle}>
                                          {item.tagId || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerId || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.scannedOn ||
                                            formatScanDateTime(row.dateOfInstallation)}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.actualShift || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerInstalledArea || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerVehicle || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerCategory || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerZone || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.readerWard || "N/A"}
                                        </td>
                                        <td style={subStatusTdStyle}>
                                          {item.statusLabel ? (
                                            <span
                                              style={{
                                                padding: "2px 8px",
                                                borderRadius: "4px",
                                                display: "inline-block",
                                                fontSize: "12px",
                                                lineHeight: 1.3,
                                                whiteSpace: "nowrap",
                                                color:
                                                  String(item.statusLabel)
                                                    .toUpperCase() === "IN"
                                                    ? "#198754"
                                                    : "#dc3545",
                                                backgroundColor:
                                                  String(item.statusLabel)
                                                    .toUpperCase() === "IN"
                                                    ? "#d1e7dd"
                                                    : "#f8d7da",
                                              }}
                                            >
                                              {item.statusLabel}
                                            </span>
                                          ) : (
                                            <StatusBadge
                                              isActive={item.isActive ?? true}
                                              compact
                                            />
                                          )}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.fromDate || "N/A"}
                                        </td>
                                        <td style={subTdStyle}>
                                          {item.toDate || "N/A"}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: FIGMA.textMuted }}>
          {hasData
            ? `Showing ${rangeStart}-${rangeEnd} of ${totalResults} results`
            : "Showing 0-0 of 0 results"}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 20,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "14px",
              color: FIGMA.textMuted,
              margin: 0,
            }}
          >
            Row per page
            <select
              value={rowsPerPage}
              disabled={isLoading}
              onChange={(e) => setReportPageSize(Number(e.target.value))}
              style={{
                appearance: "none",
                borderRadius: 6,
                border: `1px solid ${FIGMA.border}`,
                backgroundColor: "#F0F4F8",
                padding: "6px 28px 6px 12px",
                fontSize: "14px",
                color: "#374151",
                outline: "none",
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            aria-label="Pagination"
          >
            <button
              type="button"
              onClick={() => setReportPage(Math.max(1, currentPage - 1))}
              disabled={isLoading || currentPage === 1}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
              aria-label="Previous page"
            >
              <FiChevronLeft size={16} />
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                disabled={isLoading}
                onClick={() => setReportPage(page)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                  height: 32,
                  borderRadius: 6,
                  padding: "0 10px",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  backgroundColor:
                    currentPage === page ? FIGMA.navy : "#F0F4F8",
                  color: currentPage === page ? "#FFFFFF" : "#374151",
                }}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setReportPage(Math.min(totalPages, currentPage + 1))}
              disabled={isLoading || currentPage >= totalPages || totalPages === 0}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.4 : 1,
              }}
              aria-label="Next page"
            >
              <FiChevronRight size={16} />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default RfidReportsExpandableTable;
